import React, { useState } from 'react';
import {
	Box,
	Typography,
	Button,
	Paper,
	ListItem,
	ListItemText,
	Divider,
	ClickAwayListener,
	Tooltip,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { ellipsize } from 'common/utils/text';
import Container from '../Container';
import UniquePortal from './UniquePortal';
import { HEADER_BAR_PAGE_TITLE } from './targets';
import { useTranslation } from 'services/localization/useTranslation';

export type ContextAction = {
	label: string;
	description?: string;
	onClick: () => void;
	disabled?: boolean;
};
interface Props {
	title: string;
	caption?: string | JSX.Element | null;
	priority?: number;
	rightContent?: JSX.Element;
	contextActions?: ContextAction[];
	styling?: {
		fluid?: boolean;
	};
	isMobile?: boolean;
}

const SetPageTitle = (props: Props) => {
	const classes = useStyles(props);
	const [contextMenuOpen, setContextMenuOpen] = useState<boolean>(false);

	const handleToggleContextMenu = () => {
		setContextMenuOpen(!contextMenuOpen);
	};

	const handleContextMenuClose = () => {
		setContextMenuOpen(false);
	};
	const { t } = useTranslation();

	return (
		<UniquePortal target={HEADER_BAR_PAGE_TITLE} priority={props.priority || 0}>
			<Box className={classes.wrapper}>
				<Container fluid={!!props.styling?.fluid} className={classes.container}>
					<Box className={classes.titleWrapper}>
						<Typography variant="h1" className={classes.title}>
							{ellipsize(props.title, 48)}
						</Typography>
						{!!props.caption && (
							<Box className={classes.caption}>
								{typeof props.caption === 'string' ? (
									<Typography variant="caption">{props.caption}</Typography>
								) : (
									props.caption
								)}
							</Box>
						)}
					</Box>
					<Box className={classes.actionsWrapper}>
						{props.contextActions && (
							<ClickAwayListener onClickAway={handleContextMenuClose}>
								<Box className={classes.contextActionWrapper}>
									<Tooltip
										classes={{ popper: classes.hideTooltip }}
										title={t('common:more')}
										placement="bottom"
									>
										<Button className={classes.contextMenuButton} onClick={handleToggleContextMenu}>
											<span className={classes.contextMenuButtonText}>{t('common:more')}</span>
											<ArrowDropDownIcon />
										</Button>
									</Tooltip>
									{contextMenuOpen && (
										<Paper
											elevation={5}
											className={classes.contextMenu}
											onClick={() => setContextMenuOpen(false)}
										>
											{props.contextActions.map((action, index) => (
												<React.Fragment key={action.label}>
													{index !== 0 && <Divider />}
													<ListItem button onClick={action.onClick} disabled={action.disabled}>
														<ListItemText primary={action.label} secondary={action.description} />
													</ListItem>
												</React.Fragment>
											))}
										</Paper>
									)}
								</Box>
							</ClickAwayListener>
						)}
						{props.rightContent && (
							<Box className={classes.rightContentWrapper}>{props.rightContent}</Box>
						)}
					</Box>
				</Container>
			</Box>
		</UniquePortal>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		wrapper: {
			background: theme.palette.background.paper,
			borderBottomColor: theme.palette.border.paper,
			borderBottomWidth: '1px',
			borderBottomStyle: 'solid',
		},
		container: {
			display: 'flex',
			flexDirection: (props: Props) => (props.isMobile ? 'column' : 'row'),
			alignItems: (props: Props) => (props.isMobile ? 'flex-start' : 'center'),
			justifyContent: 'space-between',
			minHeight: '91px',
			paddingTop: theme.spacing(2),
			paddingBottom: theme.spacing(2),
		},
		titleWrapper: {
			flex: 1,
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'center',
			alignItems: 'flex-start',
		},
		actionsWrapper: {
			display: 'flex',
			flexDirection: 'row',
			justifyContent: 'flex-end',
			alignItems: 'center',
			marginLeft: (props: Props) => (props.isMobile ? 0 : theme.spacing(2)),
			position: 'relative',
		},
		title: {
			fontSize: '2.8rem',
			fontWeight: 400,
			margin: 0,
			marginBottom: 0,
			[theme.breakpoints.down('md')]: {
				fontSize: '2rem',
			},
		},
		caption: {
			marginTop: theme.spacing(1),
			'&:empty': {
				display: 'none',
			},
		},
		rightContentWrapper: {
			marginLeft: (props: Props) => (props.isMobile ? 0 : theme.spacing(1)),
			flex: '0 1 auto',
			display: 'flex',
			justifyContent: 'flex-end',
		},
		contextActionWrapper: {
			flex: '0 1 auto',
		},
		contextMenu: {
			background: theme.palette.background.paper,
			position: 'absolute',
			right: 0,
			top: '100%',
			width: '100vw',
			maxWidth: '300px',
			marginTop: theme.spacing(1),
		},
		contextMenuButton: {
			[theme.breakpoints.down('sm')]: {
				width: '40px',
				minWidth: 0,
				borderRadius: '20px',
				background: theme.palette.action.hover,
			},
		},
		contextMenuButtonText: {
			[theme.breakpoints.down('sm')]: {
				display: 'none',
			},
		},
		hideTooltip: {
			[theme.breakpoints.up('sm')]: {
				display: 'none',
			},
		},
	}),
);

export default SetPageTitle;
